import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Crown, Medal, Calendar as CalendarIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { apiGet } from "@/lib/api";

type PublicRow = {
  rank: number;
  instagram: string | null;
  avatar_url: string | null;
};

const PublicLeaderboard: React.FC = () => {
  const [monthYear, setMonthYear] = useState<string>("");
  const [rows, setRows] = useState<PublicRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { user, userRole } = useAuth();
  const [viewerHandle, setViewerHandle] = useState<string | null>(null);
  const [viewerRank, setViewerRank] = useState<number | null>(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load viewer Instagram handle
  useEffect(() => {
    const load = async () => {
      if (!user?.id) {
        setViewerHandle(null);
        return;
      }
      const { data } = await supabase.from("influencers").select("social_media, role").eq("id", user.id).single();
      const handle = data?.social_media || null;
      setViewerHandle(handle ? (handle.startsWith("@") ? handle : `@${handle}`) : null);
    };
    load();
  }, [user?.id]);

  const fetchData = async (overrideMonth?: string) => {
    setLoading(true);
    try {
      const url = new URL("/api/commission-balances/public-leaderboard", window.location.origin);
      url.searchParams.set("limit", "1000");
      if (overrideMonth || monthYear) {
        url.searchParams.set("month_year", (overrideMonth || monthYear).trim());
      }
      const res = await apiGet(url.pathname + url.search);
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`API Error: ${errorText.substring(0, 200)}`);
      }
      const data = await res.json();
      setMonthYear(data.month_year);
      setRows(data.results || []);
    } catch (e) {
      console.error("Error fetching leaderboard:", e);
    } finally {
      setLoading(false);
    }
  };

  // Compute viewer rank when rows or handle change
  useEffect(() => {
    if (userRole === "admin") {
      setViewerRank(0);
      return;
    }
    if (!viewerHandle || !rows || rows.length === 0) {
      setViewerRank(null);
      return;
    }
    const found = rows.find((r) => (r.instagram || "").toLowerCase() === viewerHandle.toLowerCase());
    setViewerRank(found?.rank ?? null);
  }, [rows, viewerHandle, userRole]);

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-8">
      <Card className="overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown className="h-5 w-5 text-primary" />
              <CardTitle className="text-2xl tracking-tight">Ranking de vendas</CardTitle>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground/80">Sua posição:</span>
                {viewerRank !== null ? (
                  <span className={`font-semibold ${badgeToneClass(viewerRank)}`}>#{viewerRank}</span>
                ) : (
                  <span className="text-foreground/60">—</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-4 w-4" />
                <MonthButtons monthYear={monthYear} onChange={(m) => fetchData(m)} />
              </div>
            </div>
          </div>
          <CardDescription className="mt-2">
            Parceiras que caminham com a Silva, espalhando nosso propósito por todos os cantos. Obrigado 🧡
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LeaderboardSkeleton />
          ) : rows.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="space-y-8">
              <Podium rows={rows} />
              <LeaderboardList rows={rows.slice(3)} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

function MonthButtons({ monthYear, onChange }: { monthYear: string; onChange: (m: string) => void }) {
  const now = new Date();
  const currentYear = now.getUTCFullYear();
  const currentMonth = now.getUTCMonth() + 1; // 1-based
  
  const makeKey = (y: number, m: number) => `${y}-${String(m).padStart(2, "0")}`;
  
  const monthLabels: Record<number, string> = {
    1: "Jan", 2: "Fev", 3: "Mar", 4: "Abr", 5: "Mai", 6: "Jun",
    7: "Jul", 8: "Ago", 9: "Set", 10: "Out", 11: "Nov", 12: "Dez"
  };
  
  // Generate months: show last 3 months of previous year + current month
  // If we're in January 2026, show: Oct 2025, Nov 2025, Dec 2025, Jan 2026
  const months = [];
  
  // Calculate the 4 months to show (last 3 + current)
  for (let i = 3; i >= 0; i--) {
    // Calculate month offset: go back i months from current month
    let targetYear = currentYear;
    let targetMonth = currentMonth - i;
    
    // Handle year rollover
    while (targetMonth < 1) {
      targetMonth += 12;
      targetYear -= 1;
    }
    while (targetMonth > 12) {
      targetMonth -= 12;
      targetYear += 1;
    }
    
    months.push({
      key: makeKey(targetYear, targetMonth),
      label: `${monthLabels[targetMonth]} ${targetYear}`,
      disabled: false
    });
  }

  // Default to current month
  const defaultKey = makeKey(currentYear, currentMonth);
  const selected = monthYear || defaultKey;

  return (
    <div className="inline-flex rounded-md border overflow-hidden shadow-sm">
      {months.map((m) => (
        <Button
          key={m.key}
          variant={selected === m.key ? "default" : "ghost"}
          className="rounded-none"
          disabled={m.disabled}
          onClick={() => !m.disabled && onChange(m.key)}
        >
          {m.label}
        </Button>
      ))}
    </div>
  );
}

 

function LeaderboardSkeleton() {
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="p-6 border rounded-xl bg-muted/30 animate-pulse h-44" />
        ))}
      </div>
      <div className="space-y-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-14 rounded-lg border bg-muted/30 animate-pulse" />
        ))}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-12">
      <Medal className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
      <p className="text-muted-foreground">Sem dados para este período.</p>
    </div>
  );
}

function Podium({ rows }: { rows: PublicRow[] }) {
  const winners = useMemo(() => rows.slice(0, 3), [rows]);
  if (winners.length === 0) return null;

  const placements = [1, 0, 2] as const; // order visually: 2nd, 1st, 3rd

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {placements.map((idx) => {
        const r = winners[idx];
        if (!r) return <div key={idx} />;
        const rank = r.rank;
        const ring = rankRingClass(rank);
        const bg = podiumBgClass(rank);
        const height = rank === 1 ? "sm:h-56" : rank === 2 ? "sm:h-48" : "sm:h-44";
        const profileUrl = r.instagram ? `https://instagram.com/${String(r.instagram).replace(/^@/, "")}` : undefined;
        return (
          <a
            key={r.rank}
            href={profileUrl}
            target="_blank"
            rel="noreferrer"
            className={`group block p-6 border rounded-xl ${bg} ${height} flex flex-col items-center justify-end transition-transform duration-200 will-change-transform hover:scale-[1.02] focus:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-primary/40`}
          >
            <div className="flex flex-col items-center -mt-6">
              <div className={`p-1 rounded-full ring-2 ${ring} bg-background shadow-md`}>
                <Avatar className="h-16 w-16">
                  {r.avatar_url && <AvatarImage src={r.avatar_url} alt={r.instagram || "avatar"} />}
                  <AvatarFallback>@</AvatarFallback>
                </Avatar>
              </div>
              <div className="mt-3 flex items-center gap-2">
                {rank === 1 && <Crown className="h-4 w-4 text-yellow-500" />}
                {rank === 2 && <Crown className="h-4 w-4 text-gray-400" />}
                {rank === 3 && <Crown className="h-4 w-4 text-amber-700" />}
                <span className="font-mono font-semibold text-sm truncate max-w-[180px] group-hover:text-primary transition-colors">
                  {r.instagram || "-"}
                </span>
              </div>
              <Badge className={`mt-2 text-xs ${badgeTone(rank)}`}>#{rank}</Badge>
            </div>
          </a>
        );
      })}
    </div>
  );
}

function LeaderboardList({ rows, hideAvatars = false }: { rows: PublicRow[]; hideAvatars?: boolean }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="rounded-xl border overflow-hidden">
      <ul>
        {rows.map((r) => {
          const profileUrl = r.instagram ? `https://instagram.com/${String(r.instagram).replace(/^@/, "")}` : undefined;
          return (
            <li key={`${r.rank}-${r.instagram}`}>
              <a
                href={profileUrl}
                target="_blank"
                rel="noreferrer"
                className="group flex items-center gap-4 px-4 py-3 border-b last:border-b-0 transition-colors hover:bg-muted/40 will-change-transform transform-gpu hover:scale-[1.01] focus:scale-[1.01] focus:outline-none"
              >
                <Badge className={`text-xs ${badgeTone(r.rank)} flex items-center gap-1`}>
                  {r.rank <= 3 && (
                    <Crown
                      className={`h-3 w-3 ${
                        r.rank === 1 ? "text-yellow-500" : r.rank === 2 ? "text-gray-400" : "text-amber-700"
                      }`}
                    />
                  )}
                  #{r.rank}
                </Badge>
                {!hideAvatars && r.rank <= 20 && (
                  <div className={`p-0.5 rounded-full ring-1 ${ringTone(r.rank)}`}>
                    <Avatar className="h-10 w-10">
                      {r.avatar_url && <AvatarImage src={r.avatar_url} alt={r.instagram || "avatar"} />}
                      <AvatarFallback>@</AvatarFallback>
                    </Avatar>
                  </div>
                )}
                <span className="font-mono truncate text-foreground/90 group-hover:text-primary transition-colors">
                  {r.instagram || "-"}
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function badgeTone(rank: number) {
  if (rank === 1) return "bg-yellow-500/10 text-yellow-700 dark:text-yellow-300 border-yellow-500/20";
  if (rank === 2) return "bg-gray-400/10 text-gray-700 dark:text-gray-300 border-gray-400/20";
  if (rank === 3) return "bg-amber-700/10 text-amber-700 dark:text-amber-300 border-amber-700/20";
  return "bg-primary/10 text-primary border-primary/20";
}

// Minimal tone for inline text (viewer rank)
function badgeToneClass(rank: number) {
  if (rank === 0) return "text-foreground";
  if (rank === 1) return "text-yellow-600 dark:text-yellow-300";
  if (rank === 2) return "text-gray-600 dark:text-gray-300";
  if (rank === 3) return "text-amber-700 dark:text-amber-300";
  return "text-primary";
}

function ringTone(rank: number) {
  if (rank === 1) return "ring-yellow-500/40";
  if (rank === 2) return "ring-gray-400/40";
  if (rank === 3) return "ring-amber-700/40";
  return "ring-primary/30";
}

function rankRingClass(rank: number) {
  if (rank === 1) return "ring-yellow-500/60";
  if (rank === 2) return "ring-gray-400/60";
  if (rank === 3) return "ring-amber-700/60";
  return "ring-primary/40";
}

function podiumBgClass(rank: number) {
  if (rank === 1) return "bg-gradient-to-b from-yellow-500/10 to-transparent";
  if (rank === 2) return "bg-gradient-to-b from-gray-400/10 to-transparent";
  if (rank === 3) return "bg-gradient-to-b from-amber-700/10 to-transparent";
  return "";
}

export default PublicLeaderboard;
